import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Notifications, NotificationsNone } from '@mui/icons-material';
import { useApiClient } from '../services/api';
import { Notification } from '../types/context';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  // Fetch unread notification count
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread', 'count'],
    queryFn: async () => {
      const response = await apiClient.getUnreadNotificationsCount();
      return response.success ? response.data : { count: 0 };
    },
    refetchInterval: 60000, // Auto-refresh every 60 seconds
    staleTime: 0,
  });

  // Fetch all notifications (not just unread)
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'all'],
    queryFn: async () => {
      const response = await apiClient.getNotifications(1, 20); // Fetch last 20 notifications (page 1, limit 20)
      return response.success ? response.data : [];
    },
    staleTime: 0,
    refetchOnMount: 'always', // Always refetch when dropdown opens
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const unreadCount = countData?.count || 0;
  const notifications = (notificationsData || []) as Notification[];

  // Track expanded notifications
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => apiClient.markNotificationAsRead(notificationId),
    onSuccess: () => {
      // Clear ETag cache for notifications
      apiClient.invalidateCache('notifications');
      // Invalidate and refetch notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => apiClient.markAllNotificationsAsRead(),
    onSuccess: () => {
      // Clear ETag cache for notifications
      apiClient.invalidateCache('notifications');
      // Invalidate and refetch notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close menu on outside click or Escape key
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && ref.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onDoc);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Compute menu position when opening
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
    }
  }, [open]);

  const handleNotificationClick = (notification: Notification, event: React.MouseEvent) => {
    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    // Toggle expansion only
    const newExpanded = new Set(expandedNotifications);
    if (newExpanded.has(notification.id)) {
      newExpanded.delete(notification.id);
    } else {
      newExpanded.add(notification.id);
    }
    setExpandedNotifications(newExpanded);
  };

  const handleToggleReadStatus = (notification: Notification, event: React.MouseEvent) => {
    // Prevent click from bubbling to parent
    event.preventDefault();
    event.stopPropagation();

    // Toggle read/unread
    if (notification.isRead) {
      // TODO: Implement mark as unread API endpoint if needed
      console.log('Mark as unread not yet implemented');
    } else {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const handleNavigateToEvent = (notification: Notification, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Navigate based on notification type
    if (notification.hackdayId) {
      navigate(`/hackday/${notification.hackdayId}`);
    } else if (notification.type.includes('team')) {
      navigate('/events');
    }

    setOpen(false);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'hackday_starting_soon':
        return '🚀';
      case 'submission_deadline':
        return '⏰';
      case 'team_member_added':
        return '👥';
      case 'team_member_removed':
        return '👋';
      case 'bounty_added':
        return '💰';
      case 'sponsor_message':
        return '📨';
      default:
        return '📢';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notifications"
        ref={btnRef}
        onClick={() => {
          if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const menuWidth = 320;
            const left = Math.max(8, rect.right - menuWidth + window.scrollX);
            const top = rect.bottom + window.scrollY + 8;
            setMenuPos({ top, left, width: menuWidth });
          }
          setOpen(v => !v);
        }}
        className="relative w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        title="Notifications"
      >
        {unreadCount > 0 ? (
          <Notifications className="text-gray-700" fontSize="medium" />
        ) : (
          <NotificationsNone className="text-gray-700" fontSize="medium" />
        )}

        {/* Red dot badge */}
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'absolute',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 9999
          }}
        >
          <div className="bg-white border rounded-md shadow-lg">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  disabled={markAllAsReadMutation.isPending}
                >
                  {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all read'}
                </button>
              )}
            </div>

            {/* Notifications list */}
            <div
              className="max-h-[400px] overflow-y-auto"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 #f7fafc'
              }}
            >
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  <NotificationsNone className="mx-auto mb-2 text-gray-400" fontSize="large" />
                  <p>No new notifications</p>
                </div>
              ) : (
                <div className="py-1">
                  {notifications.map((notification) => {
                    const isExpanded = expandedNotifications.has(notification.id);
                    const hasLongMessage = notification.message && notification.message.length > 100;

                    return (
                      <div
                        key={notification.id}
                        className={`border-b last:border-b-0 ${!notification.isRead ? 'bg-blue-50' : ''
                          }`}
                      >
                        <button
                          role="menuitem"
                          onClick={(e) => handleNotificationClick(notification, e)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl flex-shrink-0">
                              {getNotificationIcon(notification.type)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.isRead ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                                {notification.title}
                              </p>
                              {notification.message && (
                                <p className={`text-xs text-gray-600 mt-1 ${isExpanded ? '' : 'line-clamp-2'}`}>
                                  {notification.message}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-gray-400">
                                  {formatTimeAgo(notification.createdAt)}
                                </p>
                                {hasLongMessage && (
                                  <span className="text-xs text-blue-600 font-medium">
                                    {isExpanded ? '▼ Click to collapse' : '▶ Click to expand'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleToggleReadStatus(notification, e)}
                              className="flex-shrink-0 mt-1 hover:scale-110 transition-transform"
                              title={notification.isRead ? 'Mark as unread' : 'Mark as read'}
                              aria-label={notification.isRead ? 'Mark as unread' : 'Mark as read'}
                            >
                              <span
                                className={`w-2 h-2 rounded-full block ${notification.isRead ? 'bg-gray-300' : 'bg-blue-500'
                                  }`}
                              />
                            </button>
                          </div>
                        </button>

                        {/* Go to event button when expanded */}
                        {isExpanded && (notification.hackdayId || notification.type.includes('team')) && (
                          <div className="px-4 pb-3">
                            <button
                              onClick={(e) => handleNavigateToEvent(notification, e)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              Go to event
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
